// Module Contrats & Signatures — modèles de DÉMONSTRATION (aucun texte juridique définitif)
import { euro, fmtDate } from "./model";

export const CONTRACT_STATUS = {
  brouillon: "Brouillon", a_verifier: "À vérifier", pret: "Prêt à envoyer", envoye: "Envoyé pour signature", en_attente: "En attente de signature",
  signe: "Signé", refuse: "Refusé", expire: "Expiré", annule: "Annulé",
};
export const CONTRACT_FLOW = ["brouillon", "a_verifier", "pret", "envoye", "en_attente", "signe"];
export const CONTRACT_TYPES = { moe: "Contrat de maîtrise d'œuvre", adhesion: "Adhésion CozyHome Central ↔ Partenaire", avenant: "Avenant", document: "Document contractuel projet" };
export const DEMO_NOTICE = "MODÈLE DE DÉMONSTRATION — contenu juridique à valider avant mise en production.";

// ContractTemplate : sections verrouillées + champs modifiables + champs injectés automatiquement
export const CONTRACT_TEMPLATES = [
  {
    template_id: "tpl_moe", name: "Contrat de maîtrise d'œuvre (démo)", type: "moe", version: 1, status: "actif",
    created_at: "2026-01-15", updated_at: "2026-05-02",
    editable_fields: [
      { key: "mission", label: "Description de la mission", type: "textarea" },
      { key: "honoraires_ht", label: "Honoraires MOE (€ HT)", type: "number" },
      { key: "duree_previsionnelle", label: "Durée prévisionnelle de la mission", type: "text" },
      { key: "conditions_particulieres", label: "Conditions particulières", type: "textarea" },
    ],
    locked_sections: ["Objet du contrat", "Obligations du maître d'œuvre", "Obligations du maître d'ouvrage", "Assurances", "Résiliation", "Litiges"],
    content: [
      { title: "Parties", auto: "parties" },
      { title: "Objet du contrat", locked: true, text: "Le maître d'ouvrage confie au maître d'œuvre une mission de maîtrise d'œuvre pour le projet référencé ci-dessus. [Texte verrouillé — démonstration]" },
      { title: "Description de la mission", field: "mission" },
      { title: "Honoraires et échéancier", auto: "honoraires" },
      { title: "Durée", field: "duree_previsionnelle" },
      { title: "Obligations du maître d'œuvre", locked: true, text: "[Section verrouillée — démonstration]" },
      { title: "Obligations du maître d'ouvrage", locked: true, text: "[Section verrouillée — démonstration]" },
      { title: "Assurances", locked: true, text: "[Section verrouillée — démonstration]" },
      { title: "Conditions particulières", field: "conditions_particulieres" },
      { title: "Résiliation", locked: true, text: "[Section verrouillée — démonstration]" },
      { title: "Litiges", locked: true, text: "[Section verrouillée — démonstration]" },
    ],
  },
  {
    template_id: "tpl_avenant", name: "Avenant (démo)", type: "avenant", version: 1, status: "actif", created_at: "2026-01-15", updated_at: "2026-01-15",
    editable_fields: [{ key: "objet", label: "Objet de la modification", type: "textarea" }, { key: "incidence_financiere", label: "Incidence financière (€ HT)", type: "number" }],
    locked_sections: ["Référence au contrat d'origine", "Autres clauses"],
    content: [
      { title: "Parties", auto: "parties" },
      { title: "Référence au contrat d'origine", auto: "parent", locked: true },
      { title: "Objet de la modification", field: "objet" },
      { title: "Incidence financière", field: "incidence_financiere" },
      { title: "Autres clauses", locked: true, text: "Toutes les autres clauses du contrat d'origine demeurent inchangées. [Texte verrouillé — démonstration]" },
    ],
  },
  {
    template_id: "tpl_adhesion", name: "Adhésion CozyHome Central ↔ Partenaire (démo)", type: "adhesion", version: 1, status: "prepare", created_at: "2026-01-15", updated_at: "2026-01-15",
    editable_fields: [], locked_sections: ["Ensemble du document"], content: [{ title: "Adhésion", locked: true, text: "[Modèle préparé — géré par CozyHome Central ultérieurement]" }],
  },
];

// Préremplissage automatique depuis les données déjà présentes dans CozyHome
export const prefill = (project, template, parent) => {
  const honoraires = project.honoraires.reduce((s, h) => s + h.amountHT, 0);
  const works = project.lots.reduce((s, l) => s + l.amountHT, 0);
  const base = {
    client_nom: project.client.name, client_email: project.client.email, client_tel: project.client.phone,
    partenaire_societe: project.partner.company, partenaire_nom: project.partner.name, partenaire_code: project.partner.code,
    projet_ref: project.ref, projet_nom: project.name, projet_adresse: project.address, projet_nature: project.program,
    montant_travaux_ht: works, honoraires_ht: honoraires,
    echeancier: project.honoraires.map((h) => `${h.label} — ${euro(h.amountHT)} HT — ${fmtDate(h.due)}`),
  };
  if (template.type === "moe") return { ...base, mission: `Mission complète de maîtrise d'œuvre : ${project.program}`, duree_previsionnelle: "Conception à réception : environ 8 mois", conditions_particulieres: "" };
  if (template.type === "avenant") return { ...base, parent_id: parent?.contract_id, parent_version: parent?.version, parent_name: parent?.name, objet: "", incidence_financiere: 0 };
  return base;
};

export const contractParties = (project) => [
  { role: "partenaire", name: project.partner.name, entity: project.partner.company, signer: true },
  { role: "client", name: project.client.name, entity: "Maître d'ouvrage", signer: true },
];

// Rendu texte d'un contrat (utilisé pour l'aperçu et pour la copie immuable signée)
export const renderContract = (template, fields, project) =>
  template.content.map((s) => {
    if (s.auto === "parties") return { ...s, text: `Maître d'œuvre : ${fields.partenaire_societe} (${fields.partenaire_nom}, code ${fields.partenaire_code}).\nMaître d'ouvrage : ${fields.client_nom} — ${fields.client_email} — ${fields.client_tel}.\nProjet ${fields.projet_ref} — ${fields.projet_nom}, ${fields.projet_adresse}.` };
    if (s.auto === "honoraires") return { ...s, text: `Honoraires : ${euro(Number(fields.honoraires_ht))} HT (montant travaux estimé ${euro(fields.montant_travaux_ht)} HT).\nÉchéancier :\n${(fields.echeancier || []).map((e) => `• ${e}`).join("\n")}` };
    if (s.auto === "parent") return { ...s, text: `Contrat d'origine : ${fields.parent_name} (${fields.parent_id}, version ${fields.parent_version}).` };
    if (s.field) return { ...s, text: String(fields[s.field] ?? "") || "—" };
    return s;
  });

// Abstraction technique — aucun prestataire, aucune clé API. Implémentation simulée pour la preview.
export class SignatureProvider {
  createSignatureRequest() { throw new Error("not implemented"); }
  getSignatureStatus() { throw new Error("not implemented"); }
  cancelSignatureRequest() { throw new Error("not implemented"); }
  retrieveSignedDocument() { throw new Error("not implemented"); }
}
export class SimulatedSignatureProvider extends SignatureProvider {
  createSignatureRequest(contract) { return { request_id: `sim_${contract.contract_id}_${Date.now().toString(36)}`, status: "en_attente", provider: "simulated" }; }
  getSignatureStatus(request) { return request?.status || "en_attente"; }
  cancelSignatureRequest(request) { return { ...request, status: "annule" }; }
  retrieveSignedDocument(contract) { return contract.signed_document; }
}
export const signatureProvider = new SimulatedSignatureProvider();
